import Cookies from "js-cookie";

import { useAuth } from "@/lib/use-auth";
import { useDateRangeStore } from "./_components/date-range-picker/use-date-range-store";
import { useQuery } from "@tanstack/react-query";
import { convertUTCtoIST } from "@/utils";
import { fetchAllLeadsWithConditionQueryOptions } from "@/config/queries/emp-query-options";
import { DateRangePicker } from "./_components/date-range-picker/date-range-picker2";
import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Lead } from "./_components/leads-table/schema";
import { getColumns } from "./_components/leads-table/columns";
import { DataTable } from "./_components/leads-table/data-table";

import { searchableColumns } from "./_components/leads-table/columns";

const AllLeadsPageForEmployee = () => {
  const { data: authData } = useAuth();
  const key = Cookies.get("key") as string;

  const { dateRange } = useDateRangeStore();

  const startdate = dateRange?.from as Date;
  const enddate = dateRange?.to as Date;

  const permissions = authData?.user?.permissions as {
    1: string[];
    2: string[];
    3: string[];
  };

  const { data, isPending, isFetching } = useQuery(
    fetchAllLeadsWithConditionQueryOptions(
      key,
      {
        startdate: convertUTCtoIST(new Date(startdate.setHours(0))),
        enddate: convertUTCtoIST(new Date(enddate.setHours(23))),
      },
      () => permissions?.[2]?.includes("Leads")
    )
  );

  const leadsData = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (data === undefined || data?.leads === undefined) {
      return [];
    }

    const result = data?.leads?.map((lead) => {
      return {
        id: lead?._id,
        name: lead?.name,
        status: lead?.status,
        originalStatus: lead?.originalStatus,
        phone: lead?.phone,
        alloted: lead?.alloted,
        assignedStatus: lead?.alloted === true ? "true" : "false",
        ...(lead?.allotedAt
          ? {
              allotedAt: lead?.allotedAt as string | Date,
            }
          : {}),

        ...(lead?.allotedTo
          ? {
              allotedTo: lead?.allotedTo?.name,
              allotedToEmail: lead?.allotedTo?.email,
            }
          : { allotedTo: "No One", allotedToEmail: "" }),
        createdAt: lead?.createdAt,
        broker: lead?.broker,
      };
    });

    return result;
  }, [data, isPending, isFetching]) as {
    id: string;
    name: string;
    status: string;
    originalStatus: string;
    assignedStatus: string;
    phone: string;
    alloted: boolean;
    allotedAt: string | Date;
    createdAt: string | Date;
    broker: string;
  }[];

  const columns = useMemo<ColumnDef<Lead, unknown>[]>(() => getColumns(), []);

  return (
    <div className="p-5 h-[calc(100%-56px)]">
      {permissions?.[2]?.includes("Leads") ? (
        <div>
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-medium">All Leads</h3>

            <div className="flex items-center gap-2">
              <DateRangePicker />
            </div>
          </div>

          <div className="mt-5 mb-10 pb-10">
            <DataTable
              data={leadsData}
              columns={columns}
              searchableColumns={searchableColumns}
            />
          </div>
        </div>
      ) : (
        <div className="h-full flex items-center justify-center">
          <div className="border p-10 max-w-xl border-dashed rounded-md text-xl">
            You don't have enough permission to see all leads.
          </div>
        </div>
      )}
    </div>
  );
};

export default AllLeadsPageForEmployee;
